/** Read-Only Access / payment-grace banner shown app-wide under the TopBar. */
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/useAccess";
import { Lock, AlertTriangle } from "lucide-react";

export const AccessBanner = () => {
  const { user } = useAuth();
  const access = useAccess();
  if (!user || user.role === "admin") return null;

  if (access.read_only && access.status !== "suspended") {
    return (
      <div data-testid="access-banner-readonly" className="w-full bg-[#12102e] border-b-2 border-red-500">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-start gap-2.5 text-slate-200 max-w-3xl">
            <Lock size={16} strokeWidth={2.5} className="text-red-400 mt-0.5 shrink-0"/>
            <span data-testid="access-banner-message">
              Your trial or subscription is no longer active. Your account is currently in <b className="text-white">Read-Only Access mode</b>.
              You can view completed inspections and download existing PDFs, but all other features are disabled.
              Reactivate your subscription to restore full access — including your draft inspections.
            </span>
          </div>
          <Link to="/pricing" data-testid="access-reactivate-btn"
            className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#00F2C3] text-[#0B1020] text-xs font-black uppercase tracking-wide hover:opacity-90 shrink-0">
            Reactivate Subscription
          </Link>
        </div>
      </div>
    );
  }

  if (access.status === "payment_grace") {
    return (
      <div data-testid="access-banner-grace" className="w-full bg-amber-50 border-b-2 border-amber-400">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2 font-bold text-slate-900">
            <AlertTriangle size={16} strokeWidth={2.5} className="text-amber-600"/>
            <span data-testid="access-grace-message">
              Payment issue — your subscription has expired. {access.grace_days_left} day{access.grace_days_left === 1 ? "" : "s"} left
              in your grace period before the account becomes read-only.
            </span>
          </div>
          <Link to="/pricing" data-testid="access-update-payment-btn"
            className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#1E1B4B] text-white text-xs font-bold uppercase tracking-wide hover:bg-[#0F0E2E]">
            Update Payment Method
          </Link>
        </div>
      </div>
    );
  }
  return null;
};

export default AccessBanner;
